// Pure helper — picks Smithers' contextual suggestion line from user + progress state.
// Returns { line, action: {label, route} | null, secondary: {label, route} | null }

import { CURRICULUM } from "@/lib/curriculum";
import { phaseForLetter } from "@/lib/portal/phaseGrouping";

const LETTER_TITLES = Object.fromEntries(CURRICULUM.map((c) => [c.letter, c.title]));

function lessonPct(modProgress) {
    if (!modProgress) return 0;
    if (modProgress.completed_at) return 100;
    const lessons = modProgress.lessons || {};
    const done = Object.values(lessons).filter(Boolean).length;
    // 3 lessons per module in v1 scaffold
    return Math.min(100, Math.round((done / 3) * 100));
}

export function pickSmithersSuggestion({ user, progress }) {
    const entitlements = (user && user.entitlements) || {};
    const unlocked = entitlements.unlocked_letters || [];
    const tier = entitlements.tier;
    const isGenesis = tier === "genesis" || !!entitlements.genesis_iz_early_access;
    const lastActive = progress?.last_active_module;
    const modules = progress?.modules || {};

    // 1) Resume in-progress module
    if (lastActive && unlocked.includes(lastActive)) {
        const pct = lessonPct(modules[lastActive]);
        if (pct < 100) {
            return {
                line: `Continue building in Module ${lastActive} — your ${LETTER_TITLES[lastActive] || lastActive} build is ${pct}% complete.`,
                action: { label: `Open Module ${lastActive}`, route: `/portal/module/${lastActive}` },
                secondary: { label: "Review doctrine", route: "/portal#progress" },
            };
        }
    }

    // 2) No progress yet — start at first available letter
    const hasAnyProgress = Object.values(modules).some(
        (m) => m && (m.completed_at || Object.keys(m.lessons || {}).length > 0),
    );
    if (!hasAnyProgress && unlocked.length) {
        const first = unlocked[0];
        return {
            line: `Begin with Module ${first} — ${LETTER_TITLES[first] || first}. The foundation matters.`,
            action: { label: `Begin Module ${first}`, route: `/portal/module/${first}` },
            secondary: { label: "Open workspace", route: "/portal/workspace" },
        };
    }

    // 3) All unlocked modules complete → recommend expansion
    const allDone = unlocked.length > 0 && unlocked.every((L) => lessonPct(modules[L]) === 100);
    if (allDone) {
        // Genesis with I-Z available?
        if (isGenesis) {
            return {
                line: "Advanced doctrine awaits. Consider beginning Module I — Integration Systems.",
                action: { label: "Open Module I", route: "/portal/module/I" },
                secondary: { label: "Review modules", route: "/portal" },
            };
        }
        return {
            line: "Module path complete. Consider expanding capabilities with QUADRA Core.",
            action: { label: "Browse modules", route: "/#expansion-modules" },
            secondary: { label: "Open workspace", route: "/portal/workspace" },
        };
    }

    // 4) Genesis tier with I-Z but only just unlocked
    if (isGenesis && unlocked.includes("I")) {
        return {
            line: "I → Z advanced doctrine awaits. Begin with I — Integration Systems.",
            action: { label: "Open Module I", route: "/portal/module/I" },
            secondary: { label: "View dashboard", route: "/portal" },
        };
    }

    // 5) Fallback — pick first unlocked, no-progress module
    const next = unlocked.find((L) => lessonPct(modules[L]) < 100) || unlocked[0] || "A";
    const phase = phaseForLetter(next);
    return {
        line: `Continue your ${phase?.name || "build"} phase — Module ${next} is ready.`,
        action: { label: `Open Module ${next}`, route: `/portal/module/${next}` },
        secondary: { label: "Open workspace", route: "/portal/workspace" },
    };
}
